import { useCallback, useState } from 'react'
import { useJobStatus } from './useJobStatus'

/**
 * useReportJob — the poll-then-view state machine behind an AI-generated report
 * button (status on open → poll while a run is in flight → open the PDF when done).
 *
 * Extracted from the Case Retrospective button (CaseDetails' PostMortemButton) so a
 * second consumer — the on-demand Expertise-At-Risk report — reuses one state
 * machine instead of copying it. The status/poll/launch core is useJobStatus; this
 * hook adds the report-specific last step, opening the finished PDF. The
 * subject-specific parts are injected as three async callbacks, so the hook knows
 * nothing about which endpoint or identity it drives:
 *
 *   fetchStatus() → { status: 'none'|'generating'|'done'|'error', message? }
 *   launch()      → starts a generation run (resolves on 202, not on completion)
 *   fetchBlob()   → the finished PDF as a Blob (a 404 arrives as an Error with
 *                   `status === 404`, meaning the row said done but the bytes are gone)
 *
 * `identity` and `enabled` behave as in useJobStatus.
 *
 * @returns {{ status: string, error: string|null, opening: boolean, onAction: () => void }}
 *   status  — 'unknown' until the first status settles, then none/generating/done/error
 *   error   — a message to surface, or null
 *   opening — a PDF fetch is in flight (the "done" button is momentarily inert)
 *   onAction — the one-button dispatcher: done → open, none/error → generate, else no-op
 */
export function useReportJob({ enabled = true, identity, fetchStatus, launch, fetchBlob, pollMs }) {
  const { status, error, start, settle } = useJobStatus({
    enabled,
    identity,
    fetchStatus,
    launch,
    pollMs,
    failedMessage: 'Report generation failed.',
    launchFailedMessage: 'Could not start report generation.',
  })
  const [opening, setOpening] = useState(false)   // PDF fetch in flight

  /**
   * Fetch the report PDF and show it in a new tab.
   *
   * The endpoint needs an Authorization header, so we cannot point a tab straight at
   * its URL — we fetch the bytes and hand the tab a blob: URL instead. Two browser
   * behaviours shape the sequence: a window.open() after an await has lost the
   * user-gesture context and gets popup-blocked, so the tab is opened up front while
   * the click is still on the stack; and 'noopener' would make window.open() return
   * null (no handle to navigate), so it is omitted — the tab only ever loads a
   * same-origin blob of our own.
   */
  const openReport = useCallback(async () => {
    if (opening) return
    setOpening(true)
    settle('done')

    const tab = window.open('', '_blank')
    if (tab) {
      // Otherwise the tab sits blank for however long the fetch takes and reads as a
      // broken click.
      try { tab.document.write('Loading report…') } catch { /* not writable */ }
    }

    let url
    try {
      const blob = await fetchBlob()
      url = URL.createObjectURL(blob)
      if (tab) tab.location.href = url
      else window.open(url, '_blank', 'noopener')   // popup blocked; best effort
    } catch (e) {
      if (tab) tab.close()
      // A 404 here means the row said 'done' but the bytes are gone — put the button
      // back to offering generation rather than looping on a dead link; any other
      // error keeps 'done' (the report still exists) and just surfaces the message.
      if (e.status === 404) settle('none', 'No report has been generated yet.')
      else settle('done', e.detail || e.message || 'Could not open the report.')
    } finally {
      setOpening(false)
      // Revoking immediately races the tab's navigation and lands it on a blank page;
      // hold the blob long enough for the viewer to have read it.
      if (url) setTimeout(() => URL.revokeObjectURL(url), 60000)
    }
  }, [opening, settle, fetchBlob])

  // One button, three actions — see the consumer's button labels for the matching text.
  const onAction = useCallback(() => {
    if (status === 'done') return openReport()
    if (status === 'none' || status === 'error') return start()
    // 'generating' — the poll is already watching it; 'unknown' — not yet known.
  }, [status, openReport, start])

  return { status, error, opening, onAction }
}
